/* global routes */
(() => {
  if (window.CartOperations) return;

  let tail = Promise.resolve();
  let pending = 0;
  let blocked = false;
  let focusToRestore = null;
  const disabledControls = new Map();
  const inertCheckouts = new Map();
  const controls = [
    'cart-items button', 'cart-items input',
    'cart-drawer-items button', 'cart-drawer-items input',
    'cart-drawer-shipping-protection button',
    'cart-notification-upsell button',
    '#CartDrawer-Checkout', '#checkout',
  ].join(',');

  function lock() {
    document.querySelectorAll('.cart__dynamic-checkout-buttons').forEach((checkout) => {
      if (!inertCheckouts.has(checkout)) inertCheckouts.set(checkout, checkout.inert);
      checkout.inert = true;
    });
    document.querySelectorAll(controls).forEach((control) => {
      if (!disabledControls.has(control)) disabledControls.set(control, control.disabled);
      if (!control.disabled) control.disabled = true;
    });
  }

  function unlock() {
    if (pending || blocked) return;
    observer.disconnect();
    disabledControls.forEach((disabled, control) => { control.disabled = disabled || !!control.classList?.contains('cart-notification-upsell__add--added'); });
    disabledControls.clear();
    inertCheckouts.forEach((inert, checkout) => { checkout.inert = inert; });
    inertCheckouts.clear();
    if (focusToRestore && document.activeElement === document.body) {
      const target = document.getElementById(focusToRestore);
      const drawer = target?.closest('cart-drawer');
      if (!drawer || drawer.classList.contains('active')) target?.focus({ preventScroll: true });
    }
    focusToRestore = null;
  }

  // Section replacement creates new controls while a transaction is still pending.
  const observer = new MutationObserver(() => { if (pending || blocked) lock(); });

  function run(operation) {
    if (!pending && document.activeElement?.matches(controls)) focusToRestore = document.activeElement.id || null;
    pending += 1;
    lock();
    observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
    const result = tail.then(operation);
    tail = result.catch(() => {});
    return result.finally(() => { pending -= 1; unlock(); });
  }

  function sections() {
    const result = [];
    if (document.getElementById('CartDrawer')) result.push({ id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' });
    for (const id of ['main-cart-items', 'main-cart-footer']) {
      const element = document.getElementById(id);
      if (element) result.push({ id, section: element.dataset.id, selector: '.js-contents' });
    }
    if (document.getElementById('cart-icon-bubble')) result.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    const upsell = document.getElementById('main-cart-upsell');
    if (upsell) result.push({ id: 'main-cart-upsell', section: upsell.dataset.id, selector: '.js-contents' });
    return result;
  }

  function hasSections(data) {
    return sections().every((section) => typeof data?.sections?.[section.section] === 'string');
  }

  function render(data) {
    // Validate every visible cart surface before replacing any of them.
    const updates = sections().map((section) => {
      const html = data?.sections?.[section.section];
      const doc = html && new DOMParser().parseFromString(html, 'text/html');
      const source = doc && doc.querySelector(section.selector);
      if (!source) throw new Error('Cart section could not be rendered');
      const host = document.getElementById(section.id);
      return { target: host.querySelector(section.selector) || host, source, doc, section };
    });
    updates.forEach(({ target, source }) => { target.innerHTML = source.innerHTML; });
    const drawerUpdate = updates.find(({ target }) => target.id === 'CartDrawer');
    if (drawerUpdate) {
      const empty = drawerUpdate.doc.querySelector('cart-drawer')?.classList.contains('is-empty');
      document.querySelector('cart-drawer')?.classList.toggle('is-empty', !!empty);
    }
    const fullCart = updates.find(({ section }) => section.id === 'main-cart-items')?.doc.querySelector('cart-items');
    if (fullCart) {
      const empty = fullCart.classList.contains('is-empty');
      document.querySelector('cart-items')?.classList.toggle('is-empty', empty);
      document.getElementById('main-cart-footer')?.classList.toggle('is-empty', empty);
    }
    if (typeof data.item_count === 'number') {
      document.querySelector('cart-items')?.classList.toggle('is-empty', data.item_count === 0);
      document.getElementById('main-cart-footer')?.classList.toggle('is-empty', data.item_count === 0);
    }
    document.querySelector('cart-drawer')?.observeLayout?.();
    if (pending || blocked) lock();
  }

  async function refresh() {
    const names = sections().map((section) => section.section).join(',');
    const response = await fetch(`${routes.cart_url}?sections=${encodeURIComponent(names)}`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Cart refresh failed');
    render({ sections: await response.json() });
    blocked = false;
  }

  async function recover() {
    try {
      await refresh();
      return true;
    } catch (error) {
      // A write may have succeeded. Keep checkout locked until the shopper
      // opens the server-rendered cart instead of submitting outdated amounts.
      blocked = true;
      lock();
      document.querySelectorAll('#CartDrawer-CartErrors, #cart-errors').forEach((box) => {
        const link = document.createElement('a');
        link.href = routes.cart_url;
        link.textContent = window.cartStrings.viewCart;
        box.replaceChildren(document.createTextNode(`${window.cartStrings.error} `), link);
      });
      return false;
    }
  }

  function orphanShippingUpdates(cart) {
    const id = document.querySelector('cart-drawer')?.dataset.shippingVariantId;
    if (!id || !Array.isArray(cart.items)) return {};
    if (cart.items.some((item) => item.requires_shipping && String(item.variant_id) !== id)) return {};
    return Object.fromEntries(cart.items.filter((item) => String(item.variant_id) === id).map((item) => [item.key, 0]));
  }

  async function removeOrphanShipping(cart) {
    const updates = orphanShippingUpdates(cart);
    if (!Object.keys(updates).length) return cart;
    const response = await fetch(`${routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections: sections().map((section) => section.section), sections_url: window.location.pathname }),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error('Shipping protection could not be removed');
    return data;
  }

  // Also protect keyboard/form submission and controls inserted by section updates.
  document.addEventListener('submit', (event) => {
    if ((pending || blocked) && ['cart', 'CartDrawer-Form'].includes(event.target.id)) event.preventDefault();
  }, true);

  window.CartOperations = { run, sections, hasSections, render, refresh, recover, orphanShippingUpdates, removeOrphanShipping };
})();
